import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";
import { usePolicy } from "@/features/policies/policies";
import { AfellMark } from "@/components/AfellMark";
import { ArticleSkeleton } from "@/components/Skeletons";

/** A legal document, served by us rather than linked out to Squarespace.
 *
 * Outside MemberLayout for the same reason /apply is: this is a page somebody
 * reads, often from a consent checkbox in a form they are halfway through, and
 * offering them the directory preview in a sidebar is an invitation to lose
 * their place. The mark is the way home; there is no other navigation.
 *
 * remark-gfm is here for the Privacy Policy's cookie table — the one piece of
 * these documents that is not prose, and that CommonMark alone renders as a
 * row of literal pipes. */
export default function PolicyPage({ slug }: { slug: string }) {
  const { data, isLoading, error } = usePolicy(slug);

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
      <Link to="/" className="inline-flex items-center gap-2" aria-label="AFELL">
        <AfellMark className="size-8" />
        <span className="font-heading text-lg">AFELL</span>
      </Link>

      {isLoading && <ArticleSkeleton />}

      {error && (
        <p className="text-muted-foreground mt-10">
          This document could not be loaded. Write to us at{" "}
          <a className="underline underline-offset-4" href="mailto:afell@softnetwork.art">
            afell@softnetwork.art
          </a>{" "}
          and we will send it to you.
        </p>
      )}

      {data && (
        <article className="mt-10">
          <h1 className="font-heading text-4xl tracking-tight sm:text-5xl">{data.title}</h1>
          {data.effectiveDate && (
            <p className="text-muted-foreground mt-3 text-sm">
              Last updated{" "}
              {new Date(`${data.effectiveDate}T00:00:00`).toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          )}
          <div className="prose prose-neutral dark:prose-invert mt-8 max-w-none prose-headings:font-heading prose-h2:mt-10 prose-h2:text-xl prose-h3:text-base prose-table:text-sm">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.body}</ReactMarkdown>
          </div>
        </article>
      )}
    </div>
  );
}
